import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { getPartnerRequests, savePartnerRequest } from "../../actions/partners";
import { areaClass, labelClass, primaryButton } from "../template/templateLabels";
import { card } from "../ads/adLabels";

// The requests of cloudprint.mn's partner pages (the backend's routes/api/partners.js):
// ads on the kiosk screens (/advertise), a kiosk at someone's place (/place-kiosk) and
// buying kiosks (/buy-kiosk). Each new one is also mailed to the people the Settings
// page names for «Хамтран ажиллах хүсэлт». Staff call, then set where it stands.

const KINDS = {
	ads: { label: "Реклам байршуулах", badge: "text-green-700 bg-green-100 dark:text-green-100 dark:bg-green-700" },
	place: { label: "Киоск байршуулах", badge: "text-blue-700 bg-blue-100 dark:text-blue-100 dark:bg-blue-700" },
	buy: { label: "Киоск худалдаж авах", badge: "text-purple-700 bg-purple-100 dark:text-purple-100 dark:bg-purple-700" },
};

const TABS = [
	{ status: "new", label: "Шинэ", note: "Холбогдоогүй хүсэлтүүд. Залгаад төлөвийг нь өөрчилнө үү." },
	{ status: "contacted", label: "Холбогдсон", note: "Холбогдсон, яриа үргэлжилж буй." },
	{ status: "agreed", label: "Тохиролцсон", note: "Хамтран ажиллахаар тохиролцсон." },
	{ status: "declined", label: "Татгалзсан", note: "Аль нэг тал нь татгалзсан." },
];

// What each kind's page asks besides whom to call, in the order shown
const DETAILS = [
	["quantity", "Киоскийн тоо"],
	["siteType", "Ямар газар"],
	["address", "Хаяг"],
	["visitors", "Өдөрт ирдэг хүн"],
	["duration", "Хугацаа"],
];

// Stored in real UTC, shown in Ulaanbaatar time whatever the browser's
const timeText = (date) =>
	date ? new Date(new Date(date).getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 16).replace("T", " ") : "—";

const phoneText = (phone) => phone.replace(/^(\d{4})(\d{4})$/, "$1 $2");

const selectClass =
	"block w-full h-10 px-3 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-400 focus:ring focus:ring-purple-200 focus:ring-opacity-50 focus:outline-none dark:focus:ring-gray-500";

const Request = ({ request, onSave }) => {
	const [status, setStatus] = useState(request.status);
	const [note, setNote] = useState(request.note);
	const [busy, setBusy] = useState(false);
	const kind = KINDS[request.kind] || { label: request.kind, badge: "" };
	const changed = status !== request.status || note.trim() !== request.note;

	const save = async () => {
		setBusy(true);
		await onSave(request, status, note);
		setBusy(false);
	};

	return (
		<li className={"p-5 " + card}>
			<div className="flex flex-wrap items-start justify-between gap-2">
				<div className="min-w-0">
					<span className={"inline-block px-2 py-1 text-xs font-semibold rounded-full " + kind.badge}>{kind.label}</span>
					<h3 className="mt-2 text-lg font-semibold text-gray-800 dark:text-gray-100">{request.org}</h3>
				</div>
				<span className="text-xs text-gray-500 dark:text-gray-400">{timeText(request.createdAt)}</span>
			</div>

			<div className="grid gap-4 mt-3 text-sm md:grid-cols-2">
				<dl className="space-y-1 text-gray-700 dark:text-gray-300">
					<div className="flex gap-2">
						<dt className="text-gray-500 dark:text-gray-400">Холбогдох хүн:</dt>
						<dd className="font-medium">{request.name}</dd>
					</div>
					<div className="flex gap-2">
						<dt className="text-gray-500 dark:text-gray-400">Утас:</dt>
						<dd>
							<a href={"tel:" + request.phone} className="font-semibold text-purple-600 dark:text-purple-400 hover:underline">
								{phoneText(request.phone)}
							</a>
						</dd>
					</div>
					{request.email && (
						<div className="flex gap-2">
							<dt className="text-gray-500 dark:text-gray-400">Имэйл:</dt>
							<dd className="break-all">
								<a href={"mailto:" + request.email} className="text-purple-600 dark:text-purple-400 hover:underline">
									{request.email}
								</a>
							</dd>
						</div>
					)}
					{DETAILS.filter(([field]) => request[field]).map(([field, label]) => (
						<div key={field} className="flex gap-2">
							<dt className="text-gray-500 dark:text-gray-400">{label}:</dt>
							<dd>{request[field]}</dd>
						</div>
					))}
					{request.message && (
						<div className="pt-1">
							<dt className="text-gray-500 dark:text-gray-400">Нэмэлт мэдээлэл:</dt>
							<dd className="whitespace-pre-wrap">{request.message}</dd>
						</div>
					)}
				</dl>

				<div className="space-y-3">
					<label className="block">
						<span className={labelClass}>Төлөв</span>
						<select className={selectClass} value={status} onChange={(e) => setStatus(e.target.value)} disabled={busy}>
							{TABS.map((tab) => (
								<option key={tab.status} value={tab.status}>
									{tab.label}
								</option>
							))}
						</select>
					</label>
					<label className="block">
						<span className={labelClass}>Тэмдэглэл</span>
						<textarea
							className={areaClass}
							rows={3}
							maxLength={1000}
							value={note}
							onChange={(e) => setNote(e.target.value)}
							disabled={busy}
							placeholder="Юу ярьсан, дараа нь юу хийх"
						/>
					</label>
					<div className="flex flex-wrap items-center justify-between gap-2">
						<span className="text-xs text-gray-500 dark:text-gray-400">
							{request.updatedBy ? `${request.updatedBy} · ${timeText(request.updatedAt)}` : ""}
						</span>
						<button
							type="button"
							className={primaryButton + (!changed || busy ? " opacity-50 cursor-not-allowed" : "")}
							disabled={!changed || busy}
							onClick={save}
						>
							Хадгалах
						</button>
					</div>
				</div>
			</div>
		</li>
	);
};

const PartnerRequests = ({ getPartnerRequests, savePartnerRequest }) => {
	const [status, setStatus] = useState("new");
	const [data, setData] = useState(null);

	const load = useCallback(async () => {
		setData(null);
		setData((await getPartnerRequests(status)) || { requests: [], counts: {} });
	}, [getPartnerRequests, status]);
	useEffect(() => {
		load();
	}, [load]);

	const save = async (request, nextStatus, note) => {
		if (await savePartnerRequest(request.id, nextStatus, note)) load();
	};

	const tab = TABS.find((one) => one.status === status);

	return (
		<Admin>
			<>
				<h2 className="my-6 text-2xl font-semibold text-gray-700 dark:text-gray-200">Хамтрагчийн хүсэлт</h2>
				<section className={"mb-8 overflow-hidden " + card}>
					<div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
						<p className="text-sm text-gray-500 dark:text-gray-400">
							cloudprint.mn/advertise, /place-kiosk, /buy-kiosk хуудаснаас ирсэн хүсэлтүүд. Шинэ хүсэлт бүр Тохиргоо
							хуудсанд «Хамтран ажиллах хүсэлт» мэйлд нэрлэсэн хүмүүст бас мэйлээр очдог.
						</p>
						<div className="flex flex-wrap gap-2 mt-3">
							{TABS.map((one) => (
								<button
									key={one.status}
									type="button"
									onClick={() => setStatus(one.status)}
									className={
										"px-3 py-1 text-xs font-semibold rounded-full " +
										(one.status === status
											? "text-white bg-purple-600"
											: "text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-700")
									}
								>
									{one.label}
									{data && data.counts[one.status] > 0 ? ` (${data.counts[one.status]})` : ""}
								</button>
							))}
						</div>
					</div>
					{!data ? (
						<img src={spinner} alt="" className="w-10 mx-auto my-6" />
					) : data.requests.length === 0 ? (
						<p className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">Хүсэлт алга. {tab.note}</p>
					) : (
						<>
							<p className="px-5 pt-4 text-xs text-gray-500 dark:text-gray-400">{tab.note}</p>
							<ul className="grid gap-4 p-5">
								{data.requests.map((request) => (
									<Request key={request.id + request.status + request.note} request={request} onSave={save} />
								))}
							</ul>
						</>
					)}
				</section>
			</>
		</Admin>
	);
};

PartnerRequests.propTypes = {
	getPartnerRequests: PropTypes.func.isRequired,
	savePartnerRequest: PropTypes.func.isRequired,
};

export default connect(null, { getPartnerRequests, savePartnerRequest })(PartnerRequests);
